import React from 'react';

import {formField, newFormField} from '../FormField';
import {HiddenInput, Stack} from '../primitives';
import * as S from './style';
import {ESwitchSize, TSwitchSize} from './types';

export type TProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: TSwitchSize;
  label?: React.ReactNode;
  rightLabel?: React.ReactNode;
  topLabel?: React.ReactNode;
  description?: React.ReactNode;
  rightDescription?: React.ReactNode;
  topDescription?: React.ReactNode;
};

type TLabelProps = {
  label: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  size?: TSwitchSize;
};

const Label = React.memo<TLabelProps>(({label, description, disabled, size, readOnly}) => {
  return (
    <Stack direction="COLUMN" size="1">
      <S.Label disabled={disabled} readOnly={readOnly} size={size}>
        {label}
      </S.Label>
      {description && (
        <S.Description disabled={disabled} readOnly={readOnly} size={size}>
          {description}
        </S.Description>
      )}
    </Stack>
  );
});

const Switch = React.memo<TProps>(
  ({
    name,
    label,
    rightLabel,
    description,
    rightDescription,
    topDescription,
    onBlur,
    onFocus,
    onChange,
    className,
    id: propId,
    value,
    checked,
    disabled,
    readOnly,
    topLabel,
    size = ESwitchSize.M,
    ...props
  }) => {
    const id = propId ?? name;

    checked = checked ?? !!value;

    return (
      <Stack direction="COLUMN" size={size !== ESwitchSize.L ? '1' : '2'}>
        {topLabel && (
          <Label
            label={topLabel}
            description={topDescription}
            size={size}
            readOnly={readOnly}
            disabled={disabled}
          />
        )}
        <S.Switch
          checked={checked}
          disabled={disabled}
          readOnly={readOnly}
          htmlFor={id}
          className={className}
          size={size}
          {...props}
        >
          <HiddenInput
            id={id}
            name={name}
            type="checkbox"
            checked={checked}
            onChange={disabled || readOnly ? undefined : onChange}
            onFocus={disabled ? undefined : onFocus}
            onBlur={disabled ? undefined : onBlur}
            disabled={disabled}
          />
          {label && (
            <Label
              label={label}
              description={description}
              size={size}
              readOnly={readOnly}
              disabled={disabled}
            />
          )}
          <S.Control checked={checked} disabled={disabled} readOnly={readOnly} size={size} />
          {rightLabel && (
            <Label
              label={rightLabel}
              description={rightDescription}
              size={size}
              readOnly={readOnly}
              disabled={disabled}
            />
          )}
        </S.Switch>
      </Stack>
    );
  },
);

export default Switch;

export const SwitchField = formField<TProps, boolean>(Switch, {
  showHeader: false,
  showError: false,
});

export const NewSwitchField = newFormField<TProps>(Switch, {
  showHeader: false,
  showError: false,
});
export type {TSwitchSize};
export {ESwitchSize};
